import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Handshake, Copy, Check, Loader2, Sparkles, Rocket, CreditCard, Wallet, Clock, Landmark, ShieldCheck, Info } from "lucide-react";
import RevenueCalculator from "@/components/RevenueCalculator";

const LINK_TYPES = [
  { key: "cv_analysis", label: "Free CV Analysis", icon: Sparkles, hint: "Best top-of-funnel link — sends people to the free CV score tool." },
  { key: "free_trial", label: "Free Trial Signup", icon: Rocket, hint: "Routes straight to trial signup." },
  { key: "purchase", label: "Direct Purchase", icon: CreditCard, hint: "Skips the trial and heads to checkout." },
];

const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

function CopyField({ url, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("Couldn't copy"); }
  };
  return (
    <div className="flex items-center gap-2 min-w-0">
      <code className="flex-1 truncate text-xs bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2" data-testid={`${testid}-url`}>{url}</code>
      <Button variant="outline" size="icon" className="h-9 w-9 shrink-0" onClick={copy} data-testid={`${testid}-copy`}>
        {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" strokeWidth={1.5} />}
      </Button>
    </div>
  );
}

const STATUS_STYLE = { earning: "bg-green-100 text-green-800", subscribed: "bg-blue-100 text-blue-800", "signed up": "bg-zinc-100 text-zinc-600" };

export default function PartnerDashboard() {
  const [me, setMe] = useState(null);
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [tab, setTab] = useState("dashboard");
  const [params, setParams] = useSearchParams();

  const load = useCallback(async () => {
    try {
      const { data: m } = await api.get("/affiliates/me");
      setMe(m);
      if (m.is_affiliate) {
        const { data: d } = await api.get("/affiliates/dashboard");
        setDash(d);
      }
    } catch { toast.error("Couldn't load your affiliate dashboard."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Returning from Stripe Connect onboarding → sync status.
  useEffect(() => {
    if (params.get("connect")) {
      api.get("/affiliates/connect/status").then(() => load()).catch(() => {});
      params.delete("connect");
      setParams(params, { replace: true });
    }
  }, [params, setParams, load]);

  const connect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.post("/affiliates/connect/start");
      window.location.href = data.url;
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = (typeof detail === "string" && !detail.trim().startsWith("<")) ? detail : "Couldn't start payout onboarding. Please try again shortly.";
      toast.error(msg);
      setConnecting(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  if (!me?.is_affiliate) {
    const app = me?.application;
    return (
      <div className="max-w-lg mx-auto py-10" data-testid="partner-not-affiliate">
        <div className="text-center">
          <div className="h-12 w-12 rounded-xl bg-[#24468C]/10 flex items-center justify-center mx-auto mb-3">
            <Handshake className="h-6 w-6 text-[#24468C]" strokeWidth={1.5} />
          </div>
          <h1 className="font-heading text-2xl font-bold">Jobsmith Affiliate Program</h1>
          {app?.status === "pending" ? (
            <p className="text-muted-foreground mt-2">Your application is <Badge className="bg-amber-100 text-amber-800">under review</Badge>. We'll email you once it's decided.</p>
          ) : app?.status === "rejected" ? (
            <p className="text-muted-foreground mt-2">Your last application wasn't approved. You can re-apply after the cooldown period.</p>
          ) : (
            <p className="text-muted-foreground mt-2">Earn 20% cash commission for every subscriber you refer. Apply to get your unique links.</p>
          )}
          <Link to="/partners"><Button className="mt-5 bg-[#24468C] hover:bg-[#1d3870]" data-testid="partner-apply-cta">Apply to become an affiliate</Button></Link>
        </div>
      </div>
    );
  }

  const aff = dash?.affiliate || me.affiliate;
  const links = me.links;
  const referrals = dash?.referrals || [];
  const payouts = dash?.payouts || [];
  const pending = aff.commission_pending_cents || 0;
  const minPayout = dash?.min_payout_cents || 5000;

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="partner-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Handshake className="h-6 w-6 text-[#24468C]" strokeWidth={1.75} />
            <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Affiliate Dashboard</h1>
          </div>
          <p className="text-muted-foreground mt-1 text-sm">Your code: <strong className="tracking-widest text-[#24468C]" data-testid="affiliate-code">{aff.code}</strong></p>
        </div>
        <Badge className="bg-green-100 text-green-800">Approved</Badge>
      </div>

      <div className="flex gap-2" data-testid="partner-tabs">
        <button type="button" onClick={() => setTab("dashboard")} data-testid="partner-tab-dashboard"
          className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === "dashboard" ? "bg-[#24468C] text-white" : "bg-white border border-zinc-200 text-muted-foreground hover:text-foreground"}`}>Dashboard</button>
        <button type="button" onClick={() => setTab("calculator")} data-testid="partner-tab-calculator"
          className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === "calculator" ? "bg-[#24468C] text-white" : "bg-white border border-zinc-200 text-muted-foreground hover:text-foreground"}`}>Revenue calculator</button>
      </div>

      {tab === "calculator" ? (
        <RevenueCalculator defaultDirect={referrals.length || 10} />
      ) : (
      <>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Pending" value={money(pending)} icon={Clock} tone="gold" testid="stat-pending" />
        <Stat label="Lifetime paid" value={money(aff.commission_paid_cents)} icon={Wallet} tone="green" testid="stat-paid" />
        <Stat label="Tier 1 earnings" value={money(dash?.tier1_earnings_cents)} icon={Sparkles} tone="navy" testid="stat-t1" />
        <Stat label="Tier 2 earnings" value={money(dash?.tier2_earnings_cents)} icon={Handshake} tone="navy" testid="stat-t2" />
      </div>

      {/* Payout setup */}
      <div className="rounded-xl border border-zinc-200 bg-white p-5" data-testid="payout-setup">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-lg bg-[#24468C]/10 flex items-center justify-center shrink-0"><Landmark className="h-5 w-5 text-[#24468C]" strokeWidth={1.75} /></div>
            <div>
              <p className="font-heading font-semibold">Payouts{aff.payout_method === "paystack" ? " · Paystack (Nigeria)" : ""}</p>
              {aff.payout_method === "paystack" ? (
                <p className="text-sm text-muted-foreground" data-testid="paystack-pending-note">You'll be paid in NGN via <strong>Paystack</strong> bank transfer once your balance reaches {money(minPayout)}. Paystack payout setup is being finalised — you're approved and already earning, and we'll email you the moment it opens.</p>
              ) : aff.payouts_enabled ? (
                <p className="text-sm text-green-700 flex items-center gap-1"><ShieldCheck className="h-4 w-4" /> Connected — paid monthly once your balance reaches {money(minPayout)}.</p>
              ) : (
                <p className="text-sm text-muted-foreground">Connect a payout account (Stripe) to get paid. Identity & tax forms are collected securely by Stripe.</p>
              )}
            </div>
          </div>
          {!aff.payouts_enabled && aff.payout_method !== "paystack" && (
            <Button onClick={connect} disabled={connecting} className="bg-[#24468C] hover:bg-[#1d3870]" data-testid="connect-payouts-btn">
              {connecting ? <Loader2 className="h-4 w-4 animate-spin" /> : (aff.has_stripe_account ? "Finish payout setup" : "Set up payouts")}
            </Button>
          )}
        </div>
      </div>

      {/* Links */}
      <div className="rounded-xl border border-zinc-200 bg-white p-5 space-y-5" data-testid="affiliate-links">
        <div>
          <h2 className="font-heading font-semibold text-lg">Your share links</h2>
          <p className="text-sm text-muted-foreground">All variants use the same code — every signup and sale traces back to you.</p>
        </div>
        {LINK_TYPES.map((t) => {
          const Icon = t.icon;
          return (
            <div key={t.key} className="space-y-1.5">
              <div className="flex items-center gap-1.5 text-sm font-medium"><Icon className="h-4 w-4 text-[#24468C]" strokeWidth={1.5} /> {t.label}</div>
              <CopyField url={links[t.key]} testid={`link-${t.key}`} />
              <p className="text-xs text-muted-foreground">{t.hint}</p>
            </div>
          );
        })}
        <div className="pt-3 border-t border-zinc-100 grid sm:grid-cols-2 gap-3">
          <div className="space-y-1.5 min-w-0"><div className="text-sm font-medium">Lite (direct)</div><CopyField url={links.purchase_lite} testid="link-purchase-lite" /></div>
          <div className="space-y-1.5 min-w-0"><div className="text-sm font-medium">Pro (direct)</div><CopyField url={links.purchase_pro} testid="link-purchase-pro" /></div>
        </div>
      </div>

      {/* Referrals */}
      <div className="rounded-xl border border-zinc-200 bg-white p-4 sm:p-5" data-testid="affiliate-referrals">
        <h2 className="font-heading font-semibold text-lg mb-3">Your referrals</h2>
        {referrals.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">No referrals yet — share your links to start earning.</p>
        ) : (
          <>
            {/* Mobile: stacked cards */}
            <div className="space-y-2 md:hidden">
              {referrals.map((r, i) => (
                <div key={`m-${r.email_masked}-${r.signup_date}-${i}`} className="rounded-lg border border-zinc-200 p-3" data-testid={`referral-card-${i}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-xs truncate">{r.email_masked}</span>
                    <Badge className={STATUS_STYLE[r.status] || "bg-zinc-100"}>{r.status}</Badge>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    <Badge variant="outline" className="text-xs">Tier {r.tier}</Badge>
                    <span>Plan: <span className="text-foreground">{r.plan || "—"}</span></span>
                    <span>Joined {r.signup_date}</span>
                  </div>
                </div>
              ))}
            </div>
            {/* Desktop: table */}
            <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Referral</TableHead><TableHead>Tier</TableHead><TableHead>Plan</TableHead>
                    <TableHead>Joined</TableHead><TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {referrals.map((r, i) => (
                    <TableRow key={`${r.email_masked}-${r.signup_date}-${i}`} data-testid={`referral-row-${i}`}>
                      <TableCell className="font-mono text-xs">{r.email_masked}</TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">Tier {r.tier}</Badge></TableCell>
                      <TableCell className="text-sm">{r.plan || <span className="text-muted-foreground">—</span>}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{r.signup_date}</TableCell>
                      <TableCell><Badge className={STATUS_STYLE[r.status] || "bg-zinc-100"}>{r.status}</Badge></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </div>

      {/* Payout history */}
      {payouts.length > 0 && (
        <div className="rounded-xl border border-zinc-200 bg-white p-5" data-testid="payout-history">
          <h2 className="font-heading font-semibold text-lg mb-3">Payout history</h2>
          <div className="space-y-2">
            {payouts.map((p) => (
              <div key={p.id} className="flex items-center justify-between text-sm border-b border-zinc-100 pb-2">
                <span className="text-muted-foreground">{(p.created_at || "").slice(0, 10)}</span>
                <span className="font-medium">{money(p.amount_cents)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-xs text-muted-foreground flex items-start gap-1.5"><Info className="h-3.5 w-3.5 mt-0.5 shrink-0" /> Reminder: most regions require paid promotion to be disclosed (e.g. "#ad" or "affiliate link"). Commissions: 20% per charge, 6 months on monthly plans, one-time on annual.</p>
      </>
      )}
    </div>
  );
}

function Stat({ label, value, icon: Icon, tone, testid }) {
  const tones = {
    gold: "bg-[#E8A93C]/15 text-[#8a6410]", green: "bg-green-50 text-green-600", navy: "bg-[#24468C]/10 text-[#24468C]",
  };
  return (
    <Card className="border-zinc-200 shadow-sm" data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
          <div className={`h-7 w-7 rounded-md flex items-center justify-center ${tones[tone]}`}><Icon className="h-4 w-4" strokeWidth={1.75} /></div>
        </div>
        <p className="font-heading text-2xl font-bold mt-1">{value}</p>
      </CardContent>
    </Card>
  );
}
